import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityType } from '@common-fe/common-fe';

import { PaymentType, TransactionType } from '@/modules/transaction/transaction.types';
import theme from '@/styles/theme';

export const useGetTransactionTypeLabel = (
  type: TransactionType | PaymentType | ActivityType,
  isPosting?: boolean
) => {
  const { t } = useTranslation();

  const transactionTypeLabel = useMemo(() => {
    if (type === TransactionType.FORFEITURE) {
      return t('Forfeiture');
    }
    if (type === TransactionType.CLAIM_REPAYMENT) {
      return t('Claim Repayment');
    }
    if (type === TransactionType.PAY_CLAIM || type === PaymentType.REIMBURSEMENT) {
      return t('Reimburse Me');
    }
    if (type === TransactionType.CARD_SETTLEMENT || type === PaymentType.CARD_SWIPE) {
      return t('Card swipe');
    }
    if (type === TransactionType.NOTIONAL_FUND) {
      return t('Account Funding');
    }
    if (type === TransactionType.ROLLOVER) {
      return t('Rollover');
    }
    if (type === TransactionType.HSA_ROLLOVER) {
      return t('HSA Rollover');
    }
    if (type === TransactionType.CONTRIBUTION_PAYMENT) {
      return t('Contribution');
    }
    if (type === TransactionType.HSA_INCOMING_CONTRIBUTION) {
      return t('Employee Contribution');
    }
    if (type === TransactionType.PAYROLL || type === TransactionType.NOTIONAL_PAYROLL) {
      return isPosting ? t('Contribution') : t('Payroll');
    }
    if (type === TransactionType.INTEREST) {
      return t('Interest');
    }
    if (
      type === TransactionType.FEE ||
      type === TransactionType.ACCOUNT_MAINTENANCE_EE_FEE ||
      type === TransactionType.FEE_TRANSFER_INTERNAL ||
      type === TransactionType.FEE_TRANSFER ||
      type === TransactionType.PAPER_STATEMENT_FEE ||
      type === TransactionType.PAPER_STATEMENT_EE_FEE
    ) {
      return t('Fee');
    }
    if (type === TransactionType.PAYMENT_EE_FEE) {
      return 'Fees';
    }
    if (type === TransactionType.PAYMENT) {
      return t('Payment');
    }
    if (type === TransactionType.TRANSFER_ADJUSMENT) {
      return t('Transfer Adjustment');
    }
    if (
      type === TransactionType.INTERNAL_TRANSFER_OF_ASSETS ||
      type === TransactionType.OTHER_ROOT_INTERNAL_TRANSFER_OF_ASSETS
    ) {
      return t('Internal Transfer of Assets');
    }
    if (
      type === TransactionType.TRANSFER_OF_ASSETS ||
      type === TransactionType.INBOUND_TRANSFER_OF_ASSETS ||
      type === TransactionType.OUTBOUND_TRANSFER_OF_ASSETS
    ) {
      return t('Transfer of Assets');
    }
    if (
      type === TransactionType.INVESTMENT ||
      type === TransactionType.BUY_INVESTMENT ||
      type === TransactionType.SELL_INVESTMENT
    ) {
      return t('Investment');
    }
    if (type === PaymentType.BILL_PAY) {
      return t('Bill Pay');
    }
    if (
      type === TransactionType.HSA_INCOMING_CONTRIBUTION_REVERSE ||
      type === TransactionType.PAYROLL_REVERSE ||
      type === TransactionType.NOTIONAL_PAYROLL_REVERSE ||
      type === TransactionType.RECORD_KEEPING_PAYROLL
    ) {
      return t('Reverse Contribution');
    }
    if (type === TransactionType.HSA_ROLLOVER_REVERSE) {
      return t(' Reverse Rollover');
    }

    if (
      type === ActivityType.CLAIM_BALANCE_ADJUSTMENT ||
      type === ActivityType.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL ||
      type === ActivityType.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE
    ) {
      return t('Claims Adjustment');
    }
    if (
      type === ActivityType.ROLLOVER_BALANCE_ADJUSTMENT ||
      type === ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_CROSS_OMNIBUS ||
      type === ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL ||
      type === ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE
    ) {
      return t('Rollover Adjustment');
    }
    if (type === ActivityType.ADJUSTMENT || type == ActivityType.ADJUSTMENTS) {
      return t('Adjustment');
    }
    return '';
  }, [t, type, isPosting]);

  const color = useMemo(() => {
    if (
      type === TransactionType.PAYMENT_EE_FEE ||
      type === TransactionType.ACCOUNT_MAINTENANCE_EE_FEE ||
      type === TransactionType.FEE_TRANSFER_INTERNAL ||
      type === TransactionType.FEE_TRANSFER ||
      type === TransactionType.PAPER_STATEMENT_FEE ||
      type === TransactionType.PAPER_STATEMENT_EE_FEE
    ) {
      return theme.colors.textOnColor;
    }

    return theme.colors.textBody;
  }, [type]);

  const background = useMemo(() => {
    if (
      type === TransactionType.PAYMENT_EE_FEE ||
      type === TransactionType.ACCOUNT_MAINTENANCE_EE_FEE ||
      type === TransactionType.FEE_TRANSFER_INTERNAL ||
      type === TransactionType.FEE_TRANSFER ||
      type === TransactionType.PAPER_STATEMENT_FEE ||
      type === TransactionType.PAPER_STATEMENT_EE_FEE
    ) {
      return theme.colors.background1;
    }

    return theme.colors.legend;
  }, [type]);

  const transactionDateLabel = useMemo(() => {
    if (type === PaymentType.REIMBURSEMENT || type === PaymentType.BILL_PAY) {
      return t('Service date:');
    }
    if (type === PaymentType.CARD_SWIPE) {
      return t('Card swipe date:');
    }

    return !isPosting ? t('Transaction date:') : t('Date:');
  }, [t, type, isPosting]);

  return {
    transactionTypeLabel,
    transactionDateLabel,
    color,
    background,
  };
};
